import React from "react";
import {
  Box,
  Paper,
  Typography,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  TableContainer,
  IconButton,
  Tooltip,
  TextField,
  Button,
  MenuItem,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import RefreshIcon from "@mui/icons-material/Refresh";

const daysOrder = [
  "sunday",
  "monday",
  "tuesday",
  "wednesday",
  "thursday",
  "friday",
  "saturday",
];

export default function TimetableGrid({
  timeTableData = [],
  handleOpen,
  handleDeleteOpenModal,
  cookies,
  section,
  setSection,
  onRefresh,
  classSections,
  sectionName,
  attendanceMode = false,
  onSelectSlot,
}) {
  const days = timeTableData[0]?.days;
  const sortedDays = daysOrder?.map((name) =>
    days?.find((item) => item?.day === name),
  );

  const maxRows = Math.max(...sortedDays.map((d) => d?.slots?.length || 1));

  const skipMap = {};

  const getRowTitle = (rowIndex) => {
    let period = 0;

    for (let i = 0; i <= rowIndex; i++) {
      const slot = sortedDays.find((d) => d?.slots?.[i])?.slots?.[i];

      if (!slot) continue;

      if (slot.slotType === "class") period++;

      if (i === rowIndex) {
        if (slot.slotType === "class") return `Period ${period}`;
        if (slot.slotType === "short break") return "Break";
        if (slot.slotType === "break") return "Break";
        if (slot.slotType === "exam") return "Exam";
      }
    }

    return "";
  };

  const renderSlot = (slot, day) => {
    const hasEntry = slot.entries?.length > 0;
    const isClass = slot.slotType === "class";
    const isExam = slot.slotType === "exam";
    const isEmptyActionable = (isClass || isExam) && !hasEntry;
    const isBreak =
      slot.slotType === "break" || slot.slotType === "short break";

    const bg = isClass
      ? hasEntry
        ? "#e8f4ff"
        : "#eef7ff"
      : isExam
        ? hasEntry
          ? "#fff4cc"
          : "#fff8e1"
        : "#f5f5f5";

    const handleAssignClick = () => {
      if (isBreak) return;
      handleOpen(slot, null, timeTableData[0].timeTableId);
    };

    const handleEditClick = () => {
      handleOpen(slot, slot.entries[0], timeTableData[0].timeTableId);
    };

    const handleDeleteClick = () => {
      const payload = {
        entriesId: slot.entries[0].entriesId,
        timeTableId: timeTableData[0].timeTableId,
        userId: cookies.userId,
        slotId: slot.slotId,
        classSectionId: sectionName?.classSectionId,
      };
      handleDeleteOpenModal(payload);
    };

    const handleSelectClick = () => {
      if (isBreak) return;
      onSelectSlot({ day: day.day, ...slot });
    };

    const canSelect = (() => {
      if (!attendanceMode) return false;
      if (!(isClass || isExam)) return false;
      
      const userRole = cookies?.roleId;
      const userId = cookies?.userId;

      // Organization Coordinators can select everything
      if (userRole === "organizationCoordinator") {
        return true;
      }

      // Program Coordinators
      if (userRole === "programCoordinator") {
        // 1. Check if user is the Section Coordinator (In-charge)
        const isSectionInCharge = timeTableData[0]?.sectionCoordinator === userId;
        if (isSectionInCharge) return true;

        // 2. Otherwise, check if they are assigned to this specific slot
        return hasEntry && slot.entries[0].coordinatorId === userId;
      }

      return false;
    })();

    return (
      <Box
        key={`${day.day}-${slot.slotId}`}
        sx={{
          height: "100%",
          width: "100%",
          background: bg,
          border: isEmptyActionable ? "2px dashed #1976d2" : "none",
          display: "flex",
          flexDirection: "column",
          justifyContent: "center",
          alignItems: "center",
          textAlign: "center",
          gap: 0.5,
          p: 1,
        }}
      >
        {isEmptyActionable ? (
          <>
            <Typography fontWeight="bold" color="primary">
              {isClass ? "Unassigned Period" : "Unassigned Exam"}
            </Typography>

            <Typography variant="caption">
              {slot.startTime} - {slot.endTime}
            </Typography>

            {!attendanceMode && cookies?.roleId === "organizationCoordinator" && (
              <Tooltip title="Assign">
                <IconButton
                  size="small"
                  color="primary"
                  onClick={handleAssignClick}
                >
                  <AddIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            )}
            
            {canSelect && (
              <Button
                size="small"
                variant="contained"
                onClick={handleSelectClick}
                sx={{ 
                  mt: 1, 
                  fontSize: '10px', 
                  py: 0.2, 
                  px: 1.5,
                  borderRadius: '12px',
                  backgroundColor: '#224D99',
                  '&:hover': { backgroundColor: '#1a3a73' }
                }}
              >
                Select
              </Button>
            )}
          </>
        ) : (
          <>
            <Typography fontWeight="bold" textTransform="capitalize">
              {hasEntry ? slot.entries[0].slotName : slot.slotType}
            </Typography>

            {hasEntry && (
              <Typography variant="caption" color="text.secondary">
                {slot.entries[0].coordinatorName}
              </Typography>
            )}

            <Typography variant="caption">
              {slot.startTime} - {slot.endTime}
            </Typography>

            {canSelect && (
              <Button
                size="small"
                variant="contained"
                onClick={handleSelectClick}
                sx={{ 
                  mt: 1, 
                  fontSize: '10px', 
                  py: 0.2, 
                  px: 1.5,
                  borderRadius: '12px',
                  backgroundColor: '#224D99',
                  '&:hover': { backgroundColor: '#1a3a73' }
                }}
              >
                Select
              </Button>
            )}

            {!attendanceMode && cookies?.roleId === "organizationCoordinator" && (
              <>
                {(isClass || isExam) && (
                  <Box display="flex" gap={1}>
                    {hasEntry ? (
                      <>
                        <Tooltip title="Edit">
                          <IconButton
                            size="small"
                            color="primary"
                            onClick={handleEditClick}
                          >
                            <EditIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>

                        <Tooltip title="Delete">
                          <IconButton
                            size="small"
                            color="error"
                            onClick={handleDeleteClick}
                          >
                            <DeleteIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      </>
                    ) : (
                      <>
                        {cookies?.roleId === "organizationCoordinator" && (
                          <Tooltip title="Assign">
                            <IconButton
                              size="small"
                              color="primary"
                              onClick={handleAssignClick}
                            >
                              <AddIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        )}
                      </>
                    )}
                  </Box>
                )}
              </>
            )}
          </>
        )}
      </Box>
    );
  };

  return (
    <Box p={3}>
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          gap: 2,
          mb: 2,
          flexWrap: "wrap",
        }}
      >
        <TextField
          select
          label="Section"
          value={section}
          onChange={(e) => setSection(e.target.value)}
          size="small"
          disabled={cookies.roleId === "courseParticipant"}
          sx={{
            minWidth: 220,
            textTransform: "capitalize",
          }}
        >
          {classSections?.map((c) => (
            <MenuItem
              key={c._id}
              value={c.classSectionId}
              sx={{ textTransform: "capitalize" }}
            >
              {c.title}
            </MenuItem>
          ))}
        </TextField>

        <Typography
          variant="h5"
          sx={{
            textTransform: "capitalize",
            color: "#777",
            fontWeight: 600,
          }}
        >
          {sectionName?.title || sectionName}
        </Typography>

        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            gap: 1,
            flexWrap: "wrap",
          }}
        >
          <Tooltip title="Refresh">
            <Button
              onClick={onRefresh}
              variant="outlined"
              sx={{
                minWidth: "40px",
                width: "40px",
                height: "40px",
                borderRadius: "50%",
                p: 0,
              }}
            >
              <RefreshIcon fontSize="small" />
            </Button>
          </Tooltip>
        </Box>
      </Box>

      <TableContainer component={Paper}>
        {timeTableData?.length === 0 ? (
          <Box
            display="flex"
            flexDirection="column"
            alignItems="center"
            justifyContent="center"
            height="300px"
          >
            <Typography
              variant="h6"
              sx={{
                fontWeight: 600,
                color: "rgb(35, 71, 159)",
                mb: 1,
              }}
            >
              No Timetable Found
            </Typography>

            <Typography
              variant="body2"
              sx={{
                color: "#777",
                textAlign: "center",
                maxWidth: "320px",
              }}
            >
              No timetable data is available for the selected section.
            </Typography>
          </Box>
        ) : (
          <Table sx={{ borderCollapse: "collapse" }}>
            <TableHead>
              <TableRow sx={{ background: "#1976d2" }}>
                <TableCell
                  sx={{
                    color: "#fff",
                    fontWeight: "bold",
                    border: "1px solid #d0d7de",
                  }}
                >
                  Period
                </TableCell>

                {daysOrder.map((day) => (
                  <TableCell
                    key={day}
                    align="center"
                    sx={{
                      color: "#fff",
                      fontWeight: "bold",
                      border: "1px solid #d0d7de",
                      textTransform: "capitalize",
                    }}
                  >
                    {day}
                  </TableCell>
                ))}
              </TableRow>
            </TableHead>

            <TableBody>
              {[...Array(maxRows)].map((_, rowIndex) => (
                <TableRow key={rowIndex}>
                  <TableCell
                    sx={{
                      fontWeight: "bold",
                      textAlign: "center",
                      border: "1px solid #d0d7de",
                      minWidth: 100,
                      textTransform: "capitalize",
                    }}
                  >
                    {getRowTitle(rowIndex)}
                  </TableCell>

                  {sortedDays.map((day, dayIndex) => {
                    const key = `${dayIndex}-${rowIndex}`;

                    if (skipMap[key]) return null;

                    if (day.status === "holiday" && rowIndex === 0) {
                      for (let i = 1; i < maxRows; i++) {
                        skipMap[`${dayIndex}-${i}`] = true;
                      }

                      return (
                        <TableCell
                          key={key}
                          rowSpan={maxRows}
                          sx={{
                            border: "1px solid #d0d7de",
                            p: 0,
                          }}
                        >
                          <Box
                            sx={{
                              minHeight: maxRows * 115,
                              background: "#ffeaea",
                              display: "flex",
                              justifyContent: "center",
                              alignItems: "center",
                              flexDirection: "column",
                              textTransform: "capitalize",
                            }}
                          >
                            <Typography fontWeight="bold" color="error">
                              Holiday
                            </Typography>

                            <Typography variant="caption">
                              {day.reason}
                            </Typography>
                          </Box>
                        </TableCell>
                      );
                    }

                    const slot = day.slots[rowIndex];

                    if (!slot) {
                      return (
                        <TableCell
                          key={key}
                          sx={{
                            border: "1px solid #d0d7de",
                            height: 110,
                          }}
                        />
                      );
                    }

                    const isLastExam =
                      rowIndex === day.slots.length - 1 &&
                      slot.slotType === "exam" &&
                      day.slots.length < maxRows;

                    if (isLastExam) {
                      const span = maxRows - rowIndex;

                      for (let i = rowIndex + 1; i < maxRows; i++) {
                        skipMap[`${dayIndex}-${i}`] = true;
                      }

                      return (
                        <TableCell
                          key={key}
                          rowSpan={span}
                          sx={{
                            border: "1px solid #d0d7de",
                            p: 0,
                            height: span * 110,
                            textTransform: "capitalize",
                          }}
                        >
                          {renderSlot(slot, day)}
                        </TableCell>
                      );
                    }

                    return (
                      <TableCell
                        key={key}
                        sx={{
                          border: "1px solid #d0d7de",
                          p: 0,
                          height: 110,
                          minWidth: 180,
                          textTransform: "capitalize",
                        }}
                      >
                        {renderSlot(slot, day)}
                      </TableCell>
                    );
                  })}
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </TableContainer>
    </Box>
  );
}
